import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import guardiansService from '../services/guardiansService';
import employmentHistoryService from '../services/employmentHistoryService';
import guardianDashboardService from '../services/guardianDashboardService';
import s3Service from '../services/s3Service';
import S3DocumentUpload from '../components/S3DocumentUpload';
import api from '../api';
import styles from '../assets/guardianEditProfile.module.css';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const GuardianEditProfile = () => {
  const navigate = useNavigate();
  const { guardianId: urlGuardianId } = useParams();
  const { user } = useAuth();

  // Get guardian ID from URL params or dashboard data
  const [guardianId, setGuardianId] = useState(urlGuardianId || null);

  // Profile data state
  const [profileData, setProfileData] = useState({
    role: 'Guardian',
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    password: '',
    passwordConfirmation: '',
    photograph: null,

    jobTitle: '',
    phoneNumber: '',
    mobileNumber: '',
    address1: '',
    address2: '',
    address3: '',
    town: '',
    postcode: '',
    nationality: '',
    religion: '',
    heardAbout: '',
    gender: '',
    dateOfBirth: '',
    nationalInsuranceNumber: '',
    criminalRecord: null,
    criminalRecordDescription: '',
    medicalCondition: null,
    medicalConditionDescription: '',
    nextOfKin: '',
  });

  // Profile image preview state
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);

  // Employment history state
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [loadingEmployment, setLoadingEmployment] = useState(false);
  const [showEmploymentForm, setShowEmploymentForm] = useState(false);
  const [employmentForm, setEmploymentForm] = useState({
    date_start: '',
    date_end: '',
    still_in_employment: '0',
    company_name: '',
    address_1: '',
    address_2: '',
    address_3: '',
    town: '',
    postcode: '',
    referee_name: '',
    referee_telephone: '',
    referee_email: '',
    job_title: '',
    reason_for_leaving: '',
  });
  const [submittingEmployment, setSubmittingEmployment] = useState(false);
  const [editingEmploymentId, setEditingEmploymentId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // State for employment history table sorting
  const [employmentSortField, setEmploymentSortField] = useState("date_start");
  const [employmentSortDirection, setEmploymentSortDirection] = useState("desc");

  // Handle employment history sorting
  const handleEmploymentSort = (field) => {
    if (employmentSortField === field) {
      setEmploymentSortDirection(employmentSortDirection === "asc" ? "desc" : "asc");
    } else {
      setEmploymentSortField(field);
      setEmploymentSortDirection("asc");
    }
  };

  // Sort employment history for display
  const sortedEmploymentHistory = [...employmentHistory].sort((a, b) => {
    let aValue = a[employmentSortField];
    let bValue = b[employmentSortField];

    // Handle nested object sorting - map sortField to actual data field names
    if (employmentSortField === "company_name") {
      aValue = a.company_name || "";
      bValue = b.company_name || "";
    } else if (employmentSortField === "date_start") {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    } else if (employmentSortField === "date_end") {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    }

    if (aValue < bValue) return employmentSortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return employmentSortDirection === "asc" ? 1 : -1;
    return 0;
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployment, setSelectedEmployment] = useState(null);
  const [deletingEmploymentId, setDeletingEmploymentId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Employment form validation state
  const [employmentFormErrors, setEmploymentFormErrors] = useState({});

  // Loading and submission states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Lookup options loaded from the backend so option IDs always match the
  // current environment's lookup tables (avoids FK constraint violations).
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [religionsOptions, setReligionsOptions] = useState([]);
  const [hearAboutOptions, setHearAboutOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const [countriesRes, religionsRes, hearAboutRes] = await Promise.all([
          api.get('/countries'),
          api.get('/religions'),
          api.get('/hearabout'),
        ]);

        if (cancelled) return;

        // Render one option per label; if multiple rows share a label, keep
        // the lowest ID so the saved value stays stable.
        const uniqueByLabel = (rows) => {
          const byKey = new Map();
          for (const row of rows) {
            if (!row.label) continue;
            const key = row.label.trim().toLowerCase();
            if (!key) continue;
            const existing = byKey.get(key);
            if (!existing || row.value < existing.value) {
              byKey.set(key, { value: row.value, label: row.label.trim() });
            }
          }
          return Array.from(byKey.values());
        };

        const countries = uniqueByLabel(
          (countriesRes.data || []).map((c) => ({ value: c.id, label: c.nationality }))
        ).sort((a, b) => a.label.localeCompare(b.label));

        const religions = uniqueByLabel(
          (religionsRes.data || []).map((r) => ({ value: r.id, label: r.name }))
        ).sort((a, b) => a.label.localeCompare(b.label));

        const hearAbout = uniqueByLabel(
          (hearAboutRes.data || []).map((h) => ({ value: h.id, label: h.name }))
        );

        setCountriesOptions(countries);
        setReligionsOptions(religions);
        setHearAboutOptions(hearAbout);
      } catch (error) {
        console.error('Error loading lookup options:', error);
        toast.error('Failed to load profile options');
      }
    };

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load profile image preview from S3
  const loadProfileImagePreview = useCallback(async (imageKey) => {
    if (!imageKey) {
      setProfilePreviewUrl(null);
      return;
    }

    try {
      // Get pre-signed URL from backend
      const url = await s3Service.getFileUrlFromBackend(imageKey);
      setProfilePreviewUrl(url);
    } catch (error) {
      console.error('Error loading profile image preview:', error);
      // Fallback to direct S3 URL construction
      const fallbackUrl = s3Service.getFileUrl(imageKey);
      setProfilePreviewUrl(fallbackUrl);
    }
  }, []);

  useEffect(() => {
    const loadGuardianId = async () => {
      try {
        // If we have a guardian ID from URL params, use it
        if (urlGuardianId) {
          setGuardianId(urlGuardianId);
          return;
        }

        // Otherwise, get the guardian ID from the dashboard data
        const dashboardData = await guardianDashboardService.getDashboardData();
        console.log('Dashboard data:', dashboardData);
        if (dashboardData?.guardian?.id) {
          console.log('Setting guardian ID:', dashboardData.guardian.id);
          setGuardianId(dashboardData.guardian.id);
        } else {
          console.error(
            'Guardian ID not found in dashboard data:',
            dashboardData
          );
          toast.error('Guardian ID not found');
          navigate('/guardian-dashboard');
        }
      } catch (error) {
        console.error('Error loading guardian ID:', error);
        toast.error('Failed to load guardian data');
        navigate('/guardian-dashboard');
      }
    };

    loadGuardianId();
  }, [urlGuardianId, navigate]);

  useEffect(() => {
    if (!guardianId) return;

    loadGuardianProfile();
    loadEmploymentHistory();
  }, [guardianId]);

  const loadGuardianProfile = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading guardian profile for ID:', guardianId);
      const guardian = await guardiansService.getGuardianById(guardianId);

      if (guardian) {
        setProfileData({
          role: 'Guardian',
          title: guardian.user?.salutation || '',
          firstName: guardian.user?.first_name || '',
          lastName: guardian.user?.last_name || '',
          email: guardian.user?.email || '',
          currentPassword: '',
          password: '',
          passwordConfirmation: '',
          photograph: guardian.user?.image || null,

          jobTitle: guardian.user?.job_title || '',
          phoneNumber: guardian.user?.phone_number || '',
          mobileNumber: guardian.user?.mobile_number || '',
          address1: guardian.user?.address_1 || '',
          address2: guardian.user?.address_2 || '',
          address3: guardian.user?.address_3 || '',
          town: guardian.user?.town || '',
          postcode: guardian.user?.postcode || '',
          nationality: guardian.country_id || '',
          religion: guardian.religion_id || '',
          heardAbout: guardian.hear_about_id || '',
          gender: guardian.gender || '',
          dateOfBirth: guardian.date_of_birth
            ? new Date(guardian.date_of_birth).toISOString().split('T')[0]
            : '',
          nationalInsuranceNumber: guardian.national_insurance_number || '',
          criminalRecord: guardian.criminal_record === 1,
          criminalRecordDescription: guardian.criminal_record_description || '',
          medicalCondition: guardian.medical_condition === 1,
          medicalConditionDescription: guardian.medical_condition_description || '',
          nextOfKin: guardian.next_of_kin || '',
        });

        // Load profile image preview if exists
        if (guardian.user?.image) {
          await loadProfileImagePreview(guardian.user.image);
        }
      }
    } catch (error) {
      console.error('Error loading guardian profile:', error);
      toast.error('Failed to load guardian profile');
    } finally {
      setLoading(false);
    }
  }, [guardianId, loadProfileImagePreview]);

  const loadEmploymentHistory = async () => {
    if (!guardianId) return;

    setLoadingEmployment(true);
    try {
      const response = await employmentHistoryService.getByGuardianId(
        guardianId
      );
      setEmploymentHistory(response.data || []);
    } catch (error) {
      console.error('Error loading employment history:', error);
      toast.error('Failed to load employment history');
    } finally {
      setLoadingEmployment(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear the field's error once the user starts correcting it
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleProfilePhotoUpload = (s3Key, fileName) => {
    setProfileData((prev) => ({
      ...prev,
      photograph: s3Key,
    }));

    // Update preview URL for the newly uploaded image
    if (s3Key) {
      loadProfileImagePreview(s3Key);
    }
  };

  const handleProfilePreviewChange = (previewUrl) => {
    // This is called by S3DocumentUpload when a file is selected for preview
    setProfilePreviewUrl(previewUrl);
  };

  const handleRemoveProfilePhoto = () => {
    setProfileData((prev) => ({
      ...prev,
      photograph: null,
    }));

    // Clear preview URL
    setProfilePreviewUrl(null);
  };

  const clearPasswordFields = () => {
    setProfileData((prev) => ({
      ...prev,
      currentPassword: '',
      password: '',
      passwordConfirmation: '',
    }));
  };

  const validateForm = () => {
    const errors = {};

    // ✅ MATCHING LARAVEL FRONTEND: Only validate fields with red asterisk (label-required class)

    // Required fields (have label-required in Laravel Blade)
    if (!profileData.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!profileData.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!profileData.mobileNumber?.trim()) {
      errors.mobileNumber = 'Mobile number is required';
    }
    if (!profileData.gender) {
      errors.gender = 'Gender is required';
    }
    if (!profileData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    }

    // National Insurance Number: optional, but if provided must match UK format
    // Mirrors backend regex in gg-be/middleware/validateGuardian.js
    if (profileData.nationalInsuranceNumber?.trim()) {
      const niRegex = /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{1}$/i;
      if (!niRegex.test(profileData.nationalInsuranceNumber.trim())) {
        errors.nationalInsuranceNumber =
          'National Insurance Number must be in valid UK format (e.g., AB123456C)';
      }
    }

    // ✅ REMOVED: Nationality, Religion, Hear About - NOT required in Laravel frontend (no red asterisk)
    // ✅ REMOVED: Criminal Record, Medical Condition - NOT required in Laravel frontend (no red asterisk)
    // ✅ REMOVED: Next of Kin - NOT required in Laravel frontend (no red asterisk)

    // Conditional validation: Only validate description if Yes is selected
    if (profileData.criminalRecord === true && !profileData.criminalRecordDescription?.trim()) {
      errors.criminalRecordDescription = 'Please provide details of your criminal record';
    }

    if (profileData.medicalCondition === true && !profileData.medicalConditionDescription?.trim()) {
      errors.medicalConditionDescription = 'Please provide details of your medical condition';
    }

    // Password validation
    if (
      profileData.password ||
      profileData.passwordConfirmation ||
      profileData.currentPassword?.trim()
    ) {
      // If user wants to change password, current password is required
      if (!profileData.currentPassword?.trim()) {
        errors.currentPassword =
          'Current password is required to change password';
      }

      // If current password is provided, new password is required
      if (
        profileData.currentPassword?.trim() &&
        !profileData.password?.trim()
      ) {
        errors.password = 'New password is required';
      }

      // If new password is provided, confirmation is required
      if (
        profileData.password?.trim() &&
        !profileData.passwordConfirmation?.trim()
      ) {
        errors.passwordConfirmation = 'Password confirmation is required';
      }

      // Check if new password and confirmation match
      if (
        profileData.password?.trim() &&
        profileData.passwordConfirmation?.trim() &&
        profileData.password !== profileData.passwordConfirmation
      ) {
        errors.passwordConfirmation =
          'New password and confirmation must match';
      }

      // Password strength validation
      if (profileData.password?.trim()) {
        if (profileData.password.length < 8) {
          errors.password = 'Password must be at least 8 characters long';
        } else if (!/(?=.*[a-z])/.test(profileData.password)) {
          errors.password =
            'Password must contain at least one lowercase letter';
        } else if (!/(?=.*[A-Z])/.test(profileData.password)) {
          errors.password =
            'Password must contain at least one uppercase letter';
        } else if (!/(?=.*\d)/.test(profileData.password)) {
          errors.password = 'Password must contain at least one number';
        }
      }
    }

    // ✅ REMOVED: Employment history requirement - NOT validated in Laravel frontend

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    setSubmitting(true);

    try {
      const updateData = {
        user: {
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          salutation: profileData.title,
          job_title: profileData.jobTitle,
          phone_number: profileData.phoneNumber,
          mobile_number: profileData.mobileNumber,
          address_1: profileData.address1,
          address_2: profileData.address2,
          address_3: profileData.address3,
          town: profileData.town,
          postcode: profileData.postcode,
          image: profileData.photograph,
        },
        gender: profileData.gender,
        date_of_birth: profileData.dateOfBirth,
        national_insurance_number: profileData.nationalInsuranceNumber,
        country_id: profileData.nationality,
        religion_id: profileData.religion,
        hear_about_id: profileData.heardAbout,
        criminal_record: profileData.criminalRecord ? 1 : 0,
        criminal_record_description: profileData.criminalRecordDescription || '',
        medical_condition: profileData.medicalCondition ? 1 : 0,
        medical_condition_description: profileData.medicalConditionDescription || '',
        next_of_kin: profileData.nextOfKin,
      };

      // Add password data if user wants to change password
      if (profileData.currentPassword && profileData.password) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.password;
      }

      // Update profile data (including password if provided)
      await guardiansService.updateGuardianProfile(guardianId, updateData);

      // Clear password fields after successful submission
      clearPasswordFields();

      toast.success('Profile updated successfully');
      // Remove redirect - stay on the same page
      // navigate('/guardian-dashboard');
    } catch (error) {
      console.error('Error updating profile:', error);

      // axios only gives us "Request failed with status code 400" in
      // error.message - the useful text is on the response body.
      const serverMessage =
        error.response?.data?.message || error.response?.data?.error;

      // A rejected current password is a field error, not just a toast
      if (
        error.response?.status === 400 &&
        /current password/i.test(serverMessage || '')
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          currentPassword: serverMessage,
        }));
      }

      toast.error(serverMessage || error.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  // Employment history functions
  const handleEmploymentFormChange = (field, value) => {
    setEmploymentForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // ✅ Real-time validation: Clear field error when user starts typing
    if (employmentFormErrors[field]) {
      setEmploymentFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const resetEmploymentForm = () => {
    setEmploymentForm({
      date_start: '',
      date_end: '',
      still_in_employment: '0',
      company_name: '',
      address_1: '',
      address_2: '',
      address_3: '',
      town: '',
      postcode: '',
      referee_name: '',
      referee_telephone: '',
      referee_email: '',
      job_title: '',
      reason_for_leaving: '',
    });
    setEditingEmploymentId(null);
    setEmploymentFormErrors({}); // ✅ Clear form errors when resetting
    setShowEmploymentForm(false);
  };

  // ✅ LARAVEL VALIDATION PARITY: Employment form validation exactly matching Laravel rules
  const validateEmploymentForm = () => {
    const errors = {};

    // Required fields (matching Laravel validation)
    if (!employmentForm.company_name?.trim()) {
      errors.company_name = 'Company name is required';
    } else if (employmentForm.company_name.length > 255) {
      errors.company_name = 'Company name cannot exceed 255 characters';
    }

    if (!employmentForm.address_1?.trim()) {
      errors.address_1 = 'Address 1 is required';
    } else if (employmentForm.address_1.length > 255) {
      errors.address_1 = 'Address 1 cannot exceed 255 characters';
    }

    if (!employmentForm.town?.trim()) {
      errors.town = 'Town is required';
    } else if (employmentForm.town.length > 255) {
      errors.town = 'Town cannot exceed 255 characters';
    }

    if (!employmentForm.postcode?.trim()) {
      errors.postcode = 'Postcode is required';
    } else if (employmentForm.postcode.length > 255) {
      errors.postcode = 'Postcode cannot exceed 255 characters';
    } else {
      // UK postcode validation pattern (matching Laravel)
      const postcodePattern = /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?\s[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}$/;
      if (!postcodePattern.test(employmentForm.postcode.trim())) {
        errors.postcode = 'Please enter a valid UK postcode (e.g., SW1A 1AA)';
      }
    }

    if (!employmentForm.job_title?.trim()) {
      errors.job_title = 'Job title is required';
    } else if (employmentForm.job_title.length > 255) {
      errors.job_title = 'Job title cannot exceed 255 characters';
    }

    // Date validation (conditional, matching Laravel logic)
    if (!employmentForm.date_start?.trim()) {
      errors.date_start = 'Start date is required';
    } else {
      // Convert to d/m/Y format for Laravel compatibility
      const startDate = new Date(employmentForm.date_start);
      if (isNaN(startDate.getTime())) {
        errors.date_start = 'Please enter a valid start date';
      }
    }

    // Conditional date validation based on still_in_employment
    if (employmentForm.still_in_employment === '1') {
      // Still employed - end date should be empty or not required
      // (matching Laravel logic)
    } else {
      // Not employed - end date is required and must be after start date
      if (!employmentForm.date_end?.trim()) {
        errors.date_end = 'End date is required when not still employed';
      } else {
        const endDate = new Date(employmentForm.date_end);
        const startDate = new Date(employmentForm.date_start);
        
        if (isNaN(endDate.getTime())) {
          errors.date_end = 'Please enter a valid end date';
        } else if (employmentForm.date_start && endDate <= startDate) {
          errors.date_end = 'End date must be after start date';
        }
      }
    }

    // Optional fields with max length validation
    ['address_2', 'address_3', 'referee_name', 'referee_telephone', 'referee_email', 'reason_for_leaving'].forEach(field => {
      if (employmentForm[field] && employmentForm[field].length > 255) {
        errors[field] = `${field.replace(/_/g, ' ')} cannot exceed 255 characters`;
      }
    });

    console.log('🔍 [EMPLOYMENT FORM] Validation result:', {
      hasErrors: Object.keys(errors).length > 0,
      errors: errors,
      formData: employmentForm
    });

    return errors;
  };

  const handleEmploymentSubmit = async (e) => {
    e.preventDefault();
    setSubmittingEmployment(true);

    try {
      // ✅ LARAVEL VALIDATION PARITY: Validate form before submission
      const validationErrors = validateEmploymentForm();
      setEmploymentFormErrors(validationErrors);
      
      if (Object.keys(validationErrors).length > 0) {
        console.log('❌ [EMPLOYMENT FORM] Validation failed:', validationErrors);
        toast.error('Please fix the form errors before submitting');
        setSubmittingEmployment(false);
        return;
      }

      let response;
      if (editingEmploymentId) {
        response = await employmentHistoryService.update(
          editingEmploymentId,
          employmentForm
        );
        toast.success('Employment history updated successfully');
      } else {
        response = await employmentHistoryService.create(employmentForm);
        toast.success('Employment history added successfully');
      }

      resetEmploymentForm();
      await loadEmploymentHistory();
    } catch (error) {
      console.error('Error saving employment history:', error);
      toast.error(error.message || 'Failed to save employment history');
    } finally {
      setSubmittingEmployment(false);
    }
  };

  const handleEditEmployment = (record) => {
    setEmploymentForm({
      date_start: record.date_start
        ? new Date(record.date_start).toISOString().split('T')[0]
        : '',
      date_end: record.date_end
        ? new Date(record.date_end).toISOString().split('T')[0]
        : '',
      still_in_employment: record.still_in_employment ? '1' : '0',
      company_name: record.company_name || '',
      address_1: record.address_1 || '',
      address_2: record.address_2 || '',
      address_3: record.address_3 || '',
      town: record.town || '',
      postcode: record.postcode || '',
      referee_name: record.referee_name || '',
      referee_telephone: record.referee_telephone || '',
      referee_email: record.referee_email || '',
      job_title: record.job_title || '',
      reason_for_leaving: record.reason_for_leaving || '',
    });
    setEditingEmploymentId(record.id);
    setShowEmploymentForm(true);
  };

  const handleDeleteEmployment = (id) => {
    setDeletingEmploymentId(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteEmployment = async () => {
    if (!deletingEmploymentId) return;

    try {
      setIsDeleting(true);
      await employmentHistoryService.delete(deletingEmploymentId);
      toast.success('Employment history deleted successfully');

      setEmploymentHistory((prev) =>
        prev.filter((record) => record.id !== deletingEmploymentId)
      );
      await loadEmploymentHistory();

      setShowDeleteModal(false);
      setDeletingEmploymentId(null);
      setIsDeleting(false);
    } catch (error) {
      console.error('Error deleting employment history:', error);
      toast.error(error.message || 'Failed to delete employment history');
      setIsDeleting(false);
    }
  };

  const cancelDeleteEmployment = () => {
    setShowDeleteModal(false);
    setDeletingEmploymentId(null);
    setIsDeleting(false);
  };

  const handleCloseDeleteModal = () => {
    if (deletingEmploymentId) {
      setDeletingEmploymentId(null);
    }
    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  const handleShowEmployment = (record) => {
    setSelectedEmployment(record);
    setShowViewModal(true);
  };

  if (loading || !guardianId) {
    return (
      <div className={styles.guardianEditProfile}>
        <div className={styles.container}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            {!guardianId ? 'Loading guardian data...' : 'Loading profile...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.guardianEditProfile}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h1>Edit my profile</h1>
          <div className={styles.breadcrumb}>
            <button
              type="button"
              className={styles.breadcrumbLink}
              onClick={() => navigate('/guardian-dashboard')}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className={styles.profileForm}>
          {/* All Profile Fields Section */}
          <div className={styles.section}>
            <h2>Edit my profile</h2>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Role *</label>
                <input
                  type="text"
                  value={profileData.role}
                  disabled
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Title</label>
                <select
                  value={profileData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={styles.formSelect}
                >
                  <option value="">Please Select</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>First Name *</label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) =>
                    handleInputChange('firstName', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.firstName ? styles.inputError : ''
                  }`}
                />
                {validationErrors.firstName && (
                  <small className={styles.errorText}>
                    {validationErrors.firstName}
                  </small>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>Last Name *</label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) =>
                    handleInputChange('lastName', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.lastName ? styles.inputError : ''
                  }`}
                />
                {validationErrors.lastName && (
                  <small className={styles.errorText}>
                    {validationErrors.lastName}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Email *</label>
                <input
                  type="email"
                  value={profileData.email}
                  readOnly
                  className={`${styles.formInput} ${styles.readOnlyInput}`}
                />
                <small className={styles.helpText}>
                  Email address cannot be changed
                </small>
              </div>
              <div className={styles.formGroup}>
                <label>Mobile Number *</label>
                <input
                  type="tel"
                  value={profileData.mobileNumber}
                  onChange={(e) =>
                    handleInputChange('mobileNumber', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.mobileNumber ? styles.inputError : ''
                  }`}
                />
                {validationErrors.mobileNumber && (
                  <small className={styles.errorText}>
                    {validationErrors.mobileNumber}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Current Password</label>
                <input
                  type="password"
                  value={profileData.currentPassword}
                  onChange={(e) =>
                    handleInputChange('currentPassword', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.currentPassword ? styles.inputError : ''
                  }`}
                />
                {validationErrors.currentPassword && (
                  <small className={styles.errorText}>
                    {validationErrors.currentPassword}
                  </small>
                )}
                <small className={styles.helpText}>
                  Required if you want to change your password
                </small>
              </div>
              <div className={styles.formGroup}>
                <label>Password</label>
                <input
                  type="password"
                  value={profileData.password}
                  onChange={(e) =>
                    handleInputChange('password', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.password ? styles.inputError : ''
                  }`}
                />
                {validationErrors.password && (
                  <small className={styles.errorText}>
                    {validationErrors.password}
                  </small>
                )}
                <small className={styles.helpText}>
                  Leave empty if you don't want to change password. Must be at
                  least 8 characters with uppercase, lowercase, and number.
                </small>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Password Confirmation</label>
                <input
                  type="password"
                  value={profileData.passwordConfirmation}
                  onChange={(e) =>
                    handleInputChange('passwordConfirmation', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.passwordConfirmation
                      ? styles.inputError
                      : ''
                  }`}
                />
                {validationErrors.passwordConfirmation && (
                  <small className={styles.errorText}>
                    {validationErrors.passwordConfirmation}
                  </small>
                )}
                <small className={styles.helpText}>
                  Must match the new password above
                </small>
              </div>
              <div className={styles.formGroup}>
                <label>Photograph</label>
                <div className={styles.photoUpload}>
                  {/* Profile Image Preview - Show existing image, new preview, or default placeholder */}
                  <div className={styles.profileImagePreview}>
                    {profilePreviewUrl || profileData.photograph ? (
                      <img
                        src={
                          profilePreviewUrl ||
                          (profileData.photograph
                            ? s3Service.getFileUrl(profileData.photograph)
                            : null)
                        }
                        alt="Profile preview"
                        className={styles.profilePreviewImg}
                        onError={(e) => {
                          // Fallback to backend URL if direct S3 URL fails
                          if (profileData.photograph && !profilePreviewUrl) {
                            loadProfileImagePreview(profileData.photograph);
                          }
                        }}
                      />
                    ) : (
                      <div className={styles.profilePlaceholder}>
                        <span className={styles.profilePlaceholderText}>
                          No photo
                        </span>
                      </div>
                    )}
                  </div>
                  <S3DocumentUpload
                    folder="uploads/images/users"
                    fileName={`${Date.now()}-guardian-${guardianId || 'me'}.jpg`}
                    onUploadSuccess={handleProfilePhotoUpload}
                    onPreviewChange={handleProfilePreviewChange}
                    accept=".jpg,.jpeg,.png,.webp"
                    maxSize={100 * 1024 * 1024}
                    helpText="Choose file"
                    showPreview={true}
                    uploadedFile={profileData.photograph}
                    onRemoveFile={handleRemoveProfilePhoto}
                  />
                  <small className={styles.helpText}>
                    The photograph must be a clear headshot of true likeness of
                    yourself. The file can be no larger than 100MB
                  </small>
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Job Title</label>
                <input
                  type="text"
                  value={profileData.jobTitle}
                  onChange={(e) =>
                    handleInputChange('jobTitle', e.target.value)
                  }
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={profileData.phoneNumber}
                  onChange={(e) =>
                    handleInputChange('phoneNumber', e.target.value)
                  }
                  className={styles.formInput}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Address 1</label>
                <input
                  type="text"
                  value={profileData.address1}
                  onChange={(e) =>
                    handleInputChange('address1', e.target.value)
                  }
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Address 2</label>
                <input
                  type="text"
                  value={profileData.address2}
                  onChange={(e) =>
                    handleInputChange('address2', e.target.value)
                  }
                  className={styles.formInput}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Address 3</label>
                <input
                  type="text"
                  value={profileData.address3}
                  onChange={(e) =>
                    handleInputChange('address3', e.target.value)
                  }
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Town</label>
                <input
                  type="text"
                  value={profileData.town}
                  onChange={(e) => handleInputChange('town', e.target.value)}
                  className={styles.formInput}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Postcode</label>
                <input
                  type="text"
                  value={profileData.postcode}
                  onChange={(e) =>
                    handleInputChange('postcode', e.target.value)
                  }
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Nationality</label>
                <select
                  value={profileData.nationality}
                  onChange={(e) =>
                    handleInputChange('nationality', e.target.value)
                  }
                  className={`${styles.formSelect} ${
                    validationErrors.nationality ? styles.inputError : ''
                  }`}
                >
                  <option value="">Please Select</option>
                  {countriesOptions.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
                {validationErrors.nationality && (
                  <small className={styles.errorText}>
                    {validationErrors.nationality}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Religion</label>
                <select
                  value={profileData.religion}
                  onChange={(e) =>
                    handleInputChange('religion', e.target.value)
                  }
                  className={`${styles.formSelect} ${
                    validationErrors.religion ? styles.inputError : ''
                  }`}
                >
                  <option value="">Please Select</option>
                  {religionsOptions.map((religion) => (
                    <option key={religion.value} value={religion.value}>
                      {religion.label}
                    </option>
                  ))}
                </select>
                {validationErrors.religion && (
                  <small className={styles.errorText}>
                    {validationErrors.religion}
                  </small>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>How did you hear about Global Guardians?</label>
                <select
                  value={profileData.heardAbout}
                  onChange={(e) =>
                    handleInputChange('heardAbout', e.target.value)
                  }
                  className={`${styles.formSelect} ${
                    validationErrors.heardAbout ? styles.inputError : ''
                  }`}
                >
                  <option value="">Please Select</option>
                  {hearAboutOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {validationErrors.heardAbout && (
                  <small className={styles.errorText}>
                    {validationErrors.heardAbout}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Gender *</label>
                <select
                  value={profileData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className={`${styles.formInput} ${
                    validationErrors.gender ? styles.inputError : ''
                  }`}
                >
                  <option value="">Please Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {validationErrors.gender && (
                  <small className={styles.errorText}>
                    {validationErrors.gender}
                  </small>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>Date Of Birth *</label>
                <input
                  type="date"
                  value={profileData.dateOfBirth}
                  onChange={(e) =>
                    handleInputChange('dateOfBirth', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.dateOfBirth ? styles.inputError : ''
                  }`}
                />
                {validationErrors.dateOfBirth && (
                  <small className={styles.errorText}>
                    {validationErrors.dateOfBirth}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>National Insurance Number</label>
                <input
                  type="text"
                  value={profileData.nationalInsuranceNumber}
                  onChange={(e) =>
                    handleInputChange('nationalInsuranceNumber', e.target.value)
                  }
                  className={`${styles.formInput} ${
                    validationErrors.nationalInsuranceNumber ? styles.inputError : ''
                  }`}
                />
                {validationErrors.nationalInsuranceNumber && (
                  <small className={styles.errorText}>
                    {validationErrors.nationalInsuranceNumber}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Any Criminal Record?</label>
                <div
                  className={`${styles.inlineRadios} ${
                    validationErrors.criminalRecord ? styles.hasError : ''
                  }`}
                >
                  <label>
                    <input
                      type="radio"
                      name="criminalRecord"
                      checked={profileData.criminalRecord === false}
                      onChange={() => {
                        handleInputChange('criminalRecord', false);
                        // Clear description when "No" is selected
                        handleInputChange('criminalRecordDescription', '');
                      }}
                    />{' '}
                    No
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="criminalRecord"
                      checked={profileData.criminalRecord === true}
                      onChange={() => handleInputChange('criminalRecord', true)}
                    />{' '}
                    Yes
                  </label>
                </div>
                {validationErrors.criminalRecord && (
                  <small className={styles.errorText}>
                    {validationErrors.criminalRecord}
                  </small>
                )}
              </div>
            </div>

            {/* Conditional Criminal Record Description Field */}
            {profileData.criminalRecord === true && (
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Criminal Record Description *</label>
                  <textarea
                    value={profileData.criminalRecordDescription}
                    onChange={(e) =>
                      handleInputChange('criminalRecordDescription', e.target.value)
                    }
                    className={`${styles.formInput} ${styles.textarea} ${
                      validationErrors.criminalRecordDescription ? styles.inputError : ''
                    }`}
                    rows="4"
                    placeholder="Please provide details of your criminal record..."
                  />
                  {validationErrors.criminalRecordDescription && (
                    <small className={styles.errorText}>
                      {validationErrors.criminalRecordDescription}
                    </small>
                  )}
                </div>
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Any Medical Condition?</label>
                <div
                  className={`${styles.inlineRadios} ${
                    validationErrors.medicalCondition ? styles.hasError : ''
                  }`}
                >
                  <label>
                    <input
                      type="radio"
                      name="medicalCondition"
                      checked={profileData.medicalCondition === false}
                      onChange={() => {
                        handleInputChange('medicalCondition', false);
                        // Clear description when "No" is selected
                        handleInputChange('medicalConditionDescription', '');
                      }}
                    />{' '}
                    No
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="medicalCondition"
                      checked={profileData.medicalCondition === true}
                      onChange={() =>
                        handleInputChange('medicalCondition', true)
                      }
                    />{' '}
                    Yes
                  </label>
                </div>
                {validationErrors.medicalCondition && (
                  <small className={styles.errorText}>
                    {validationErrors.medicalCondition}
                  </small>
                )}
              </div>
            </div>

            {/* Conditional Medical Condition Description Field */}
            {profileData.medicalCondition === true && (
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Medical Condition Description *</label>
                  <textarea
                    value={profileData.medicalConditionDescription}
                    onChange={(e) =>
                      handleInputChange('medicalConditionDescription', e.target.value)
                    }
                    className={`${styles.formInput} ${styles.textarea} ${
                      validationErrors.medicalConditionDescription ? styles.inputError : ''
                    }`}
                    rows="4"
                    placeholder="Please provide details of your medical condition..."
                  />
                  {validationErrors.medicalConditionDescription && (
                    <small className={styles.errorText}>
                      {validationErrors.medicalConditionDescription}
                    </small>
                  )}
                </div>
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Details of Next Kin</label>
                <textarea
                  value={profileData.nextOfKin}
                  onChange={(e) =>
                    handleInputChange('nextOfKin', e.target.value)
                  }
                  className={`${styles.formInput} ${styles.textarea} ${
                    validationErrors.nextOfKin ? styles.inputError : ''
                  }`}
                  rows="3"
                />
                {validationErrors.nextOfKin && (
                  <small className={styles.errorText}>
                    {validationErrors.nextOfKin}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formActions}>
              {/* Show validation summary if there are errors */}
              {Object.keys(validationErrors).length > 0 && (
                <div className={styles.validationSummary}>
                  <p className={styles.validationSummaryTitle}>
                    Please fix the following errors before saving:
                  </p>
                  <ul className={styles.validationSummaryList}>
                    {Object.entries(validationErrors).map(([field, error]) => (
                      <li key={field} className={styles.validationSummaryItem}>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                type="submit"
                className={styles.btnSubmit}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save my profile'}
              </button>

              <small className={styles.helpText}>
                Click to save your profile. All required fields will be
                validated when you submit.
              </small>
            </div>
          </div>

          {/* Employment History Section */}
          <div className={styles.employmentHistorySection}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Employment history</h3>
              <button
                type="button"
                className={styles.btnAddEmployment}
                onClick={() => setShowEmploymentForm(true)}
              >
                + Add employment
              </button>
            </div>

            <div className={styles.employmentNote}>
              Global Guardians requires you to complete your employment history
              to cover the past 2 years, in accordance with British Standard
              BS8584 screening requirements. All guardians provide this
              information to make sure the people living in our properties are
              responsible, working people. Please click on the ‘Add employment’
              button to begin.
            </div>

            <div className={styles.tableControls}>
              <div className={styles.showEntries}>
                <label>Show:</label>
                <select defaultValue="10">
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
                <span>entries</span>
              </div>
              <div className={styles.searchBox}>
                <label>Search:</label>
                <input type="text" placeholder="Search employment history..." />
              </div>
            </div>

            <div className={styles.employmentTableContainer}>
              <div className={styles.employmentTable}>
                <table>
                  <thead>
                    <tr>
                      <th 
                        className="sortable"
                        onClick={() => handleEmploymentSort("company_name")}
                        style={{ cursor: "pointer" }}
                      >
                        Company name<SortIcon direction={employmentSortField === "company_name" ? employmentSortDirection : null} isActive={employmentSortField === "company_name"} />
                      </th>
                      <th 
                        className="sortable"
                        onClick={() => handleEmploymentSort("date_start")}
                        style={{ cursor: "pointer" }}
                      >
                        Date start<SortIcon direction={employmentSortField === "date_start" ? employmentSortDirection : null} isActive={employmentSortField === "date_start"} />
                      </th>
                      <th 
                        className="sortable"
                        onClick={() => handleEmploymentSort("date_end")}
                        style={{ cursor: "pointer" }}
                      >
                        Date end<SortIcon direction={employmentSortField === "date_end" ? employmentSortDirection : null} isActive={employmentSortField === "date_end"} />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingEmployment ? (
                      <tr>
                        <td colSpan="4" className={styles.loadingCell}>
                          Loading employment history...
                        </td>
                      </tr>
                    ) : employmentHistory.length === 0 ? (
                      <tr>
                        <td colSpan="4" className={styles.emptyCell}>
                          No employment history found. Click "Add employment" to
                          get started.
                        </td>
                      </tr>
                    ) : (
                      sortedEmploymentHistory.map((record) => (
                        <tr
                          key={record.id}
                          className={
                            deletingEmploymentId === record.id && isDeleting
                              ? styles.deletingRow
                              : ''
                          }
                        >
                          <td>
                            {deletingEmploymentId === record.id &&
                            isDeleting ? (
                              <div className={styles.deletingIndicator}>
                                <span className={styles.spinner}></span>
                                Deleting...
                              </div>
                            ) : (
                              record.company_name
                            )}
                          </td>
                          <td>
                            {record.date_start
                              ? new Date(record.date_start).toLocaleDateString(
                                  'en-GB'
                                )
                              : 'N/A'}
                          </td>
                          <td>
                            {record.still_in_employment
                              ? 'Still working there'
                              : record.date_end
                              ? new Date(record.date_end).toLocaleDateString(
                                  'en-GB'
                                )
                              : 'N/A'}
                          </td>
                          <td className={styles.actionButtons}>
                            <button
                              type="button"
                              className={`${styles.btnAction} ${styles.btnShow}`}
                              onClick={() => handleShowEmployment(record)}
                              disabled={
                                deletingEmploymentId === record.id && isDeleting
                              }
                            >
                              Show
                            </button>
                            <button
                              type="button"
                              className={`${styles.btnAction} ${styles.btnEdit}`}
                              onClick={() => handleEditEmployment(record)}
                              disabled={
                                deletingEmploymentId === record.id && isDeleting
                              }
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className={`${styles.btnAction} ${styles.btnRemove}`}
                              onClick={() => handleDeleteEmployment(record.id)}
                              disabled={
                                deletingEmploymentId === record.id && isDeleting
                              }
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className={styles.tableFooter}>
                <div className={styles.entriesInfo}>
                  Showing {employmentHistory.length > 0 ? 1 : 0} to{' '}
                  {employmentHistory.length} of {employmentHistory.length}{' '}
                  entries
                </div>
                <div className={styles.pagination}>
                  <button disabled>Previous</button>
                  <span className={styles.currentPage}>1</span>
                  <button disabled>Next</button>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Employment Form Modal */}
        {showEmploymentForm && (
          <div className={styles.employmentFormModal}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4>
                  {editingEmploymentId
                    ? 'Edit Employment History'
                    : 'Add Employment History'}
                </h4>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={resetEmploymentForm}
                >
                  ×
                </button>
              </div>

              <form
                onSubmit={handleEmploymentSubmit}
                className={styles.employmentForm}
              >
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Start Date *</label>
                    <input
                      type="date"
                      value={employmentForm.date_start || ''}
                      onChange={(e) =>
                        handleEmploymentFormChange('date_start', e.target.value)
                      }
                      className={employmentFormErrors.date_start ? styles.inputError : ''}
                      required
                    />
                    {employmentFormErrors.date_start && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.date_start}
                      </small>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label>
                      Date End{employmentForm.still_in_employment === '0' ? ' *' : ''}
                    </label>
                    <input
                      type="date"
                      value={employmentForm.date_end}
                      onChange={(e) =>
                        handleEmploymentFormChange('date_end', e.target.value)
                      }
                      className={employmentFormErrors.date_end ? styles.inputError : ''}
                      disabled={employmentForm.still_in_employment === '1'}
                      required={employmentForm.still_in_employment === '0'}
                    />
                    {employmentFormErrors.date_end && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.date_end}
                      </small>
                    )}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Still in employment</label>
                    <select
                      value={employmentForm.still_in_employment}
                      onChange={(e) =>
                        handleEmploymentFormChange(
                          'still_in_employment',
                          e.target.value
                        )
                      }
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Company name *</label>
                    <input
                      type="text"
                      value={employmentForm.company_name}
                      onChange={(e) =>
                        handleEmploymentFormChange(
                          'company_name',
                          e.target.value
                        )
                      }
                      className={employmentFormErrors.company_name ? styles.inputError : ''}
                      required
                    />
                    {employmentFormErrors.company_name && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.company_name}
                      </small>
                    )}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Address 1 *</label>
                    <input
                      type="text"
                      value={employmentForm.address_1}
                      onChange={(e) =>
                        handleEmploymentFormChange('address_1', e.target.value)
                      }
                      className={employmentFormErrors.address_1 ? styles.inputError : ''}
                      required
                    />
                    {employmentFormErrors.address_1 && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.address_1}
                      </small>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label>Address 2</label>
                    <input
                      type="text"
                      value={employmentForm.address_2}
                      onChange={(e) =>
                        handleEmploymentFormChange('address_2', e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Address 3</label>
                    <input
                      type="text"
                      value={employmentForm.address_3}
                      onChange={(e) =>
                        handleEmploymentFormChange('address_3', e.target.value)
                      }
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Town *</label>
                    <input
                      type="text"
                      value={employmentForm.town}
                      onChange={(e) =>
                        handleEmploymentFormChange('town', e.target.value)
                      }
                      className={employmentFormErrors.town ? styles.inputError : ''}
                      required
                    />
                    {employmentFormErrors.town && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.town}
                      </small>
                    )}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Postcode *</label>
                    <input
                      type="text"
                      value={employmentForm.postcode}
                      onChange={(e) =>
                        handleEmploymentFormChange('postcode', e.target.value)
                      }
                      className={employmentFormErrors.postcode ? styles.inputError : ''}
                      required
                    />
                    {employmentFormErrors.postcode && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.postcode}
                      </small>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label>Job title *</label>
                    <input
                      type="text"
                      value={employmentForm.job_title}
                      onChange={(e) =>
                        handleEmploymentFormChange('job_title', e.target.value)
                      }
                      className={employmentFormErrors.job_title ? styles.inputError : ''}
                      required
                    />
                    {employmentFormErrors.job_title && (
                      <small className={styles.errorText}>
                        {employmentFormErrors.job_title}
                      </small>
                    )}
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Referee name</label>
                    <input
                      type="text"
                      value={employmentForm.referee_name}
                      onChange={(e) =>
                        handleEmploymentFormChange(
                          'referee_name',
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Referee telephone</label>
                    <input
                      type="tel"
                      value={employmentForm.referee_telephone}
                      onChange={(e) =>
                        handleEmploymentFormChange(
                          'referee_telephone',
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Referee email</label>
                    <input
                      type="email"
                      value={employmentForm.referee_email}
                      onChange={(e) =>
                        handleEmploymentFormChange(
                          'referee_email',
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Reason for leaving</label>
                    <textarea
                      value={employmentForm.reason_for_leaving}
                      onChange={(e) =>
                        handleEmploymentFormChange(
                          'reason_for_leaving',
                          e.target.value
                        )
                      }
                      rows="3"
                    />
                  </div>
                </div>

                <div className={styles.employmentForm}>
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      className={styles.btnCancel}
                      onClick={resetEmploymentForm}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={styles.btnSubmit}
                      disabled={submittingEmployment}
                    >
                      {submittingEmployment ? 'Saving...' : 'Save Employment'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Employment Modal */}
        {showViewModal && selectedEmployment && (
          <div className={styles.employmentFormModal}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4>Employment History Details</h4>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={() => setShowViewModal(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.employmentViewContent}>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Date start:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.date_start
                      ? new Date(
                          selectedEmployment.date_start
                        ).toLocaleDateString('en-GB')
                      : 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Date end:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.still_in_employment
                      ? 'Still working there'
                      : selectedEmployment.date_end
                      ? new Date(
                          selectedEmployment.date_end
                        ).toLocaleDateString('en-GB')
                      : 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Company name:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.company_name || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Address 1:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.address_1 || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Address 2:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.address_2 || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Address 3:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.address_3 || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Town:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.town || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Postcode:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.postcode || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Referee name:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.referee_name || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Referee telephone:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.referee_telephone || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Referee email:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.referee_email || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Job title:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.job_title || 'N/A'}
                  </div>
                </div>
                <div className={styles.viewRow}>
                  <div className={styles.viewLabel}>Reason for leaving:</div>
                  <div className={styles.viewValue}>
                    {selectedEmployment.reason_for_leaving || 'N/A'}
                  </div>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.btnCancel}
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className={styles.employmentFormModal}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4>Confirm Delete</h4>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={handleCloseDeleteModal}
                  disabled={isDeleting}
                >
                  ×
                </button>
              </div>

              <div className={styles.deleteConfirmationContent}>
                <p>Are you sure you want to delete this employment record?</p>
                <p>
                  <strong>
                    {employmentHistory.find(
                      (r) => r.id === deletingEmploymentId
                    )?.company_name || 'Unknown Company'}
                  </strong>
                </p>
                <p>This action cannot be undone.</p>
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.btnCancel}
                  onClick={cancelDeleteEmployment}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.btnSubmit} ${styles.btnDanger}`}
                  onClick={confirmDeleteEmployment}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className={styles.spinner}></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuardianEditProfile;
